/**
 * @file 错误码 → 用户文案映射
 *
 * @why 契约规定「前端按 code 分支，不解析 message」（docs/错误码.md 使用约定 1）：
 *      后端 message 只做兜底展示，前端必须按 code 给出稳定、可对齐四端的文案，
 *      否则同一错误在教师端/后台/CRM 会出现三套说法（PRD 12 章「相同状态保持一致」）。
 */

import { ApiError } from '../api';

/** 已定义文案的错误码表；新增错误码时同步 docs/错误码.md。 */
const CODE_TEXT: Record<string, string> = {
  SYS_INTERNAL_ERROR: '服务出错了，请稍后重试',
  SYS_NOT_FOUND: '接口不存在，请确认版本是否匹配',
  SYS_METHOD_NOT_ALLOWED: '请求方式不支持',
  SYS_RATE_LIMITED: '请求过于频繁，请稍后退避重试',
  SYS_NOT_READY: '依赖未就绪，请稍后重试',
  SYS_UNHEALTHY: '服务内部状态异常，需要重启',
  SYS_DEPENDENCY_UNAVAILABLE: '下游依赖不可用，请稍后重试',
  VALIDATION_FAILED: '提交内容有误，请检查标红字段',
  VALIDATION_PAGINATION_INVALID: '分页参数超出范围',
  AUTH_INVALID_CREDENTIALS: '账号或密码错误',
  AUTH_SECOND_FACTOR_REQUIRED: '请输入短信验证码',
  AUTH_SECOND_FACTOR_INVALID: '验证码错误',
  AUTH_SECOND_FACTOR_EXPIRED: '验证码已过期，请重新获取',
  AUTH_SECOND_FACTOR_LOCKED: '验证码错误次数过多，请重新登录',
  AUTH_SMS_SEND_TOO_FREQUENT: '验证码发送过于频繁，请稍后重试',
  AUTH_SMS_PHONE_MISSING: '账号未绑定手机号，请联系管理员',
  AUTH_TOKEN_INVALID: '登录状态无效，请重新登录',
  AUTH_TOKEN_EXPIRED: '登录已过期，正在尝试自动续期',
  AUTH_REFRESH_INVALID: '登录已失效，请重新登录',
  AUTH_PASSWORD_CHANGE_REQUIRED: '请先修改初始密码',
  AUTH_PASSWORD_POLICY_VIOLATION: '新密码不符合复杂度要求',
  AUTH_PASSWORD_REUSED: '新密码不能与旧密码相同',
  AUTH_ACCOUNT_DISABLED: '账号已停用，请联系管理员',
  AUTH_ACCOUNT_LOCKED: '账号已锁定，请等待解锁',
  AUTH_SESSION_EXPIRED: '会话已失效，请重新登录',
  RBAC_PERMISSION_DENIED: '无权访问该资源',
  RBAC_DATA_SCOPE_DENIED: '该数据超出你的可见范围',
  RBAC_ROLE_DISABLED: '权限组已停用',
  RBAC_SCOPE_UNRESOLVED: '数据范围无法解析，请联系管理员',
  ORG_CAMPUS_NOT_FOUND: '校区不存在',
  ORG_CAMPUS_DISABLED: '校区已停用',
  USER_NOT_FOUND: '用户不存在',
  USER_ACCOUNT_DUPLICATED: '账号已存在',
  USER_PHONE_DUPLICATED: '手机号已被占用',
  USER_ROLE_REQUIRED: '请至少选择一个权限组',
  DICT_NOT_FOUND: '字典不存在',
  DICT_ITEM_DISABLED: '该字典项已停用',
};

/**
 * @what 这些错误码只做行内展示，不打全局 Toast
 * @why 登录页自己会在表单上方展示原因；健康检查的 503 属于预期状态，
 *      全局 Toast 会变成噪声（每秒刷新一次就弹一次）。
 */
const SILENT_CODES = new Set(['SYS_NOT_READY', 'AUTH_SECOND_FACTOR_REQUIRED', 'AUTH_TOKEN_EXPIRED']);

/** 展示用文案。 */
export interface ErrorText {
  /** 兜底标题（未命中错误码时）。 */
  title: string;
  /** 主要文案。 */
  message: string;
  /** 错误码，便于用户报障。 */
  code: string | null;
  /** 追踪号，便于按日志检索。 */
  traceId: string | null;
  /** 字段级明细。 */
  details: { field: string; reason: string }[];
}

/**
 * @what 把未知异常归一成可展示文案
 * @param error 任意异常
 * @return ErrorText 展示用结构
 */
export function describeError(error: unknown): ErrorText {
  if (error instanceof ApiError) {
    const mapped = CODE_TEXT[error.code];
    return {
      title: mapped ? '操作未完成' : '请求失败',
      message: mapped ?? error.message,
      code: error.code,
      traceId: error.traceId || null,
      details: error.details.flatMap((item) =>
        item.field || item.reason
          ? [{ field: item.field ?? '-', reason: item.reason ?? '-' }]
          : [],
      ),
    };
  }
  if (error instanceof Error) {
    return { title: '本地错误', message: error.message, code: null, traceId: null, details: [] };
  }
  return { title: '未知错误', message: String(error), code: null, traceId: null, details: [] };
}

/**
 * @what 是否应该弹全局 Toast
 * @param error 任意异常
 * @return boolean true=弹
 */
export function shouldToast(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return !SILENT_CODES.has(error.code);
}
